import PropTypes from "prop-types"
import React, { createContext, useCallback, useEffect, useState } from "react"

import CollapsibleBar from "src/components/CollapsibleBar"
import InsightCard from "src/components/InsightCard"
import InsightSummary from "src/components/InsightSummary"
import Tabs from "src/components/Tabs"

import { useTracker } from "src/hooks/use_tracker"

import { updateUrlQueryParams } from "src/utils/url/parsing/marina"

import ContractGroupsTab from "./ContractGroupsTab"
import ContractsTab from "./ContractsTab"
import TemplatesTab from "./TemplatesTab"

export const ManageContractsContext = createContext(null)
const refreshRate = 300 * 1000 // 5 minutes

const ManageContracts = ({
  archivedGroupsPagePath,
  canCreateNewContractGroup,
  canExport,
  canUpdateContractGroup,
  defaultPageLimit,
  hasNoCompleteTemplates,
  hasLegacyDocuments,
  filterOptions,
  insights,
  manageDocumentsPath,
  marinaName,
  newContractGroupPath,
  templates,
  templatesPath,
}) => {
  const urlParams = new URLSearchParams(window.location.search)
  const activeTab = urlParams.get("active_tab")
  const newGroup = urlParams.get("newGroup")
  const status = urlParams.get("status")
  const page = urlParams.get("page")
  const sortField = urlParams.get("sort_field")
  const sortDirection = urlParams.get("sort_direction")
  const [currentTab, setCurrentTab] = useState(0)
  const [currentGroupPage, setCurrentGroupPage] = useState(
    page && activeTab === "groups" ? parseInt(page) : 1
  )
  const [groupStatusView, setGroupStatusView] = useState(
    status && activeTab === "groups" ? status : "active"
  )
  const [templateStatusView, setTemplateStatusView] = useState(
    status && activeTab === "templates" ? status : "active"
  )
  const [groupSortField, setGroupSortField] = useState(
    sortField && activeTab === "groups" ? sortField : "updated_at"
  )
  const [groupSortDirection, setGroupSortDirection] = useState(
    sortDirection && activeTab === "groups" ? sortDirection : "DESC"
  )
  const tracker = useTracker()
  const tabName = useCallback((tab) => {
    switch (tab) {
      case 0:
        return "templates"
      case 1:
        return "groups"
      case 2:
        return "contracts"
      default:
        return "groups"
    }
  }, [])

  const setActiveTabParam = useCallback(
    (tab) => {
      const url = new URL(window.location.href)
      url.searchParams.set("active_tab", tabName(tab))
      window.history.replaceState(null, null, url.toString())
    },
    [tabName]
  )

  const handleTabChange = (tab) => {
    setCurrentTab(tab)
    if (tab === 2) {
      setActiveTabParam(tab)
    } else {
      updateUrlQueryParams({ active_tab: tabName(tab) })
    }
    tracker.trackEvent(`contracts:${tabName(tab)}_tab_pressed`)
  }

  useEffect(() => {
    if (activeTab === "templates") {
      setCurrentTab(0)
      updateUrlQueryParams({
        active_tab: tabName(0),
        status: templateStatusView,
      })
    } else if (activeTab === "contracts") {
      setCurrentTab(2)
      setActiveTabParam(2)
    } else {
      setCurrentTab(1)
      updateUrlQueryParams({
        active_tab: tabName(1),
        status: groupStatusView,
        sort_field: groupSortField,
        sort_direction: groupSortDirection,
      })
    }
  }, [
    activeTab,
    currentGroupPage,
    groupSortDirection,
    groupSortField,
    groupStatusView,
    setActiveTabParam,
    tabName,
    templateStatusView,
  ])

  return (
    <ManageContractsContext.Provider
      value={{
        archivedGroupsPagePath,
        canCreateNewContractGroup,
        canUpdateContractGroup,
        currentGroupPage,
        defaultPageLimit,
        groupSortDirection,
        groupSortField,
        groupStatusView,
        hasNoCompleteTemplates,
        hasLegacyDocuments,
        manageDocumentsPath,
        marinaName,
        newContractGroupPath,
        newGroup,
        refreshRate,
        setCurrentGroupPage,
        setGroupSortDirection,
        setGroupSortField,
        setGroupStatusView,
        setTemplateStatusView,
        templates,
        templatesPath,
        templateStatusView,
      }}
    >
      <div className="bg-white px-0 xl:px-6 xl:pt-2">
        {insights && (
          <div className="mb-4 px-4 xl:px-0 print:hidden">
            <CollapsibleBar
              title={
                <span className="text-blue-800">
                  {insights.year} Insights <i className="fa-solid fa-stars" />{" "}
                </span>
              }
              ariaLabel={`Toggle ${insights.year} Insights panel`}
              accent="bg-gradient-to-r from-blue-500 via-blue-900 to-blue-700"
              summary={
                <InsightSummary
                  items={[
                    {
                      value: insights.avgDaysToSign,
                      label: "avg days to sign",
                    },
                    { value: insights.onAutopay, label: "on autopay" },
                    {
                      value: `${insights.completedByBoater}%`,
                      label: "signed online",
                    },
                    {
                      value: `${insights.allDocuments}%`,
                      label: "complete docs",
                    },
                  ]}
                />
              }
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <InsightCard
                  icon="fa-md-schedule"
                  title="Average days to sign"
                  value={insights.avgDaysToSign}
                  description={`${insights.closedWithinDay}% signed within 24 hours`}
                />
                <InsightCard
                  icon="fa-arrows-rotate"
                  iconStyle="solid"
                  title="Contracts on autopay"
                  value={insights.onAutopay}
                  description={`Out of ${insights.signedCount} signed this year`}
                />
                <InsightCard
                  icon="fa-person"
                  iconStyle="solid"
                  title="Signed by boater online"
                  value={`${insights.completedByBoater}%`}
                  description={`Remaining ${
                    100 - insights.completedByBoater
                  }% signed in house`}
                />
                <InsightCard
                  icon="fa-md-description"
                  title="Complete documentation"
                  value={`${insights.allDocuments}%`}
                  description="Insurance & registration docs"
                />
              </div>
            </CollapsibleBar>
          </div>
        )}
        <Tabs
          selectedIndex={currentTab}
          onChange={(tab) => handleTabChange(tab)}
        >
          <div className="flex w-full flex-col justify-between lg:flex-row print:hidden">
            <div className="order-2 flex justify-center border-b lg:order-1 lg:border-none">
              <Tabs.TabList>
                <Tabs.Tab title="Templates" />
                <Tabs.Tab title="Groups" />
                <Tabs.Tab title="Contracts" />
              </Tabs.TabList>
            </div>
          </div>
          <hr className="mt-0 print:hidden" />
          <Tabs.Panels>
            <Tabs.Panel>
              <TemplatesTab />
            </Tabs.Panel>
            <Tabs.Panel>
              <ContractGroupsTab />
            </Tabs.Panel>
            <Tabs.Panel>
              <ContractsTab
                filterOptions={filterOptions}
                canExport={canExport}
              />
            </Tabs.Panel>
          </Tabs.Panels>
        </Tabs>
      </div>
    </ManageContractsContext.Provider>
  )
}

ManageContracts.propTypes = {
  archivedGroupsPagePath: PropTypes.string.isRequired,
  filterOptions: PropTypes.shape({
    contractGroupOptions: PropTypes.array,
    sortOptions: PropTypes.array,
    statusOptions: PropTypes.array,
  }).isRequired,
  canCreateNewContractGroup: PropTypes.bool.isRequired,
  canExport: PropTypes.bool.isRequired,
  canUpdateContractGroup: PropTypes.bool.isRequired,
  defaultPageLimit: PropTypes.number.isRequired,
  hasNoCompleteTemplates: PropTypes.bool.isRequired,
  hasLegacyDocuments: PropTypes.bool.isRequired,
  insights: PropTypes.shape({
    year: PropTypes.string,
    avgDaysToSign: PropTypes.number,
    closedWithinDay: PropTypes.number,
    onAutopay: PropTypes.number,
    signedCount: PropTypes.number,
    completedByBoater: PropTypes.number,
    allDocuments: PropTypes.number,
  }),
  manageDocumentsPath: PropTypes.string.isRequired,
  marinaName: PropTypes.string.isRequired,
  newContractGroupPath: PropTypes.string.isRequired,
  templates: PropTypes.array.isRequired,
  templatesPath: PropTypes.string.isRequired,
}

export default ManageContracts
